/**
 * REFERENCE LIBRARY MODULE — v2
 * Interactive textbook diagrams with:
 *   - Explanation panels with process breakdowns
 *   - Exam-style MCQ questions per diagram
 *   - Flashcard rapid-fire system
 *   - Hotspot overlays
 *   - Study progress tracking
 */

const REFERENCES = [
    {
        id: 'ref-press-sys',
        title: 'Pressure Fuel System (Low Wing)',
        category: 'fuel-systems',
        image: 'diagrams/ref_pressure_system.png',
        source: 'Jeppesen FIG 07-03',
        description: 'Typical fuel system layout for a low-wing aircraft with auxiliary boost pumps and engine-driven pumps.',
        process: 'Fuel flows by gravity and pump pressure from wing tanks → selector valve → strainer → boost pump → engine-driven pump → carburetor. The auxiliary (boost) pump is electrically driven and used for starting, takeoff, landing, and as a backup.',
        keyPoints: [
            'Low-wing aircraft REQUIRE a fuel pump (gravity alone insufficient)',
            'Boost pump = fuel boost pump = auxiliary pump (electrically driven)',
            'Engine-driven pump is the PRIMARY source of fuel pressure in flight',
            'May also be a fuel injection unit (servo) instead of a carburetor'
        ],
        examQs: [
            { q: 'In a low-wing aircraft, what provides fuel pressure during engine start?', opts: ['Engine-driven pump', 'Gravity feed', 'Electric boost pump', 'Venturi suction'], correct: 2, explain: 'The engine-driven pump is not yet running. The electric boost pump provides initial fuel pressure.' },
            { q: 'What is another name for the auxiliary fuel pump?', opts: ['Wobble pump', 'Fuel boost pump', 'Injector pump', 'Transfer pump'], correct: 1, explain: 'The auxiliary pump is also known as a fuel boost pump — it is usually electrically driven.' },
            { q: 'Why do low-wing aircraft need a fuel pump?', opts: ['For better fuel economy', 'Gravity cannot deliver fuel upward to the engine', 'To prevent vapor lock only', 'Required by regulation only'], correct: 1, explain: 'With fuel tanks below the engine, gravity alone cannot push fuel upward; pumps are essential.' }
        ],
        flashcard: { front: 'Why do low-wing aircraft REQUIRE fuel pumps?', back: 'Because fuel tanks are in the wings BELOW the engine — gravity alone cannot deliver fuel upward to the carburetor.' }
    },
    {
        id: 'ref-solenoid',
        title: 'Solenoid Selector Valve',
        category: 'components',
        image: 'diagrams/ref_solenoid_valve.png',
        source: 'Jeppesen FIG 15-22',
        description: 'Internal cross-section of a solenoid-operated selector valve used for rapid fuel tank switching.',
        process: 'Cockpit switch energizes the opening solenoid → pulls valve plunger up → spring-loaded closing solenoid holds until de-energized. Opens and closes much faster than motor-driven valves.',
        keyPoints: [
            'Two solenoids: one OPENING, one CLOSING',
            'Springs return valve to closed position if power is lost (fail-safe)',
            'Much quicker response than motor-driven selector valves',
            'Activated by cockpit electrical switch'
        ],
        examQs: [
            { q: 'What is the primary advantage of a solenoid selector valve over a motor-driven valve?', opts: ['Lower cost', 'Faster operation', 'Higher fuel flow', 'Better filtration'], correct: 1, explain: 'Solenoid valves open and close much quicker than motor-driven valves.' },
            { q: 'What happens to a solenoid selector valve if electrical power is lost?', opts: ['Valve stays open', 'Valve returns to closed (spring return)', 'Valve jams in current position', 'Backup battery takes over'], correct: 1, explain: 'Springs return the valve to the closed/safe position — this is a fail-safe design.' }
        ],
        flashcard: { front: 'How does a solenoid selector valve work?', back: 'Cockpit switch energizes the OPENING solenoid → pulls plunger → springs hold CLOSED as default (fail-safe). Much faster than motor-driven valves.' }
    },
    {
        id: 'ref-carb-sch',
        title: 'Float-Type Carburetor — Full View',
        category: 'schematics',
        image: 'diagrams/ref_carb_schematic.png',
        source: 'Avotek Fig 2-4-11',
        description: 'Complete float-type carburetor cross-section showing all major metering circuits.',
        process: 'Fuel enters through the fuel inlet → passes the needle valve → fills the float bowl. As air flows through the venturi, pressure drops → draws fuel up through the main discharge nozzle. The float regulates fuel level by opening/closing the needle valve.',
        keyPoints: [
            'Float controls fuel level by actuating needle valve',
            'Venturi creates low pressure that draws fuel from the bowl',
            'Throttle valve controls AIR volume (not fuel directly)',
            'Accelerator pump provides enrichment during rapid throttle advance',
            'Manual mixture control adjusts fuel flow for altitude compensation'
        ],
        examQs: [
            { q: 'What component directly controls the fuel level in the float bowl?', opts: ['Throttle valve', 'Float and needle valve', 'Main metering jet', 'Mixture control'], correct: 1, explain: 'The float rises with fuel level and mechanically closes the needle valve to stop flow.' },
            { q: 'What creates the pressure differential that causes fuel to flow from the discharge nozzle?', opts: ['Fuel pump pressure', 'Gravity', 'Venturi effect (low pressure in the throat)', 'Engine vacuum only'], correct: 2, explain: 'The venturi restricts airflow, creating a low-pressure area that draws fuel up through the nozzle.' },
            { q: 'The throttle valve primarily controls:', opts: ['Fuel volume', 'Air volume entering the engine', 'Mixture ratio', 'Fuel pressure'], correct: 1, explain: 'The throttle (butterfly) valve controls the VOLUME OF AIR. Fuel follows proportionally due to the venturi effect.' }
        ],
        flashcard: { front: 'What does the throttle valve control — air or fuel?', back: 'The throttle valve controls AIR volume. Fuel is pulled proportionally by the venturi effect. The mixture control handles fuel directly.' }
    },
    {
        id: 'ref-carb-cutaway',
        title: 'Carburetor 3D Cutaway Photo',
        category: 'cutaways',
        image: 'diagrams/ref_carb_cutaway.png',
        source: 'Avotek Fig 2-4-12',
        description: 'Real-world 3D photo cutaway of an aircraft float carburetor showing casting detail.',
        process: 'This is a physical cutaway of the MA-4SPA. Note the brass float, the throttle butterfly at the top, the mixture control rod on the left, and the accelerator pump linkage on the right.',
        keyPoints: [
            'Brass float is visible in the lower chamber',
            'Throttle butterfly disc visible at top opening',
            'Accelerator pump linkage on the right side',
            'Mixture control linkage on the left side',
            'Cast aluminum body with machined passages'
        ],
        examQs: [
            { q: 'What material is the float typically made of in older carburetors?', opts: ['Plastic', 'Brass', 'Steel', 'Rubber'], correct: 1, explain: 'Older floats are brass (hollow). Modern ones may be composite. A brass float that leaks will sink and cause flooding.' },
            { q: 'If a brass float develops a leak, what happens?', opts: ['Engine runs lean', 'Engine floods (runs rich)', 'No effect', 'Engine runs at correct mixture'], correct: 1, explain: 'A leaking float fills with fuel and sinks → needle valve stays open → fuel overflows → rich/flooding condition.' }
        ],
        flashcard: { front: 'What happens when a brass float LEAKS?', back: 'It fills with fuel → sinks → cannot close needle valve → fuel floods into the venturi → RICH/flooding condition. Check by immersing float in hot water and looking for bubbles.' }
    },
    {
        id: 'ref-idle-jet',
        title: 'Idle Circuit & Throttle Location',
        category: 'schematics',
        image: 'diagrams/ref_idle_jet.png',
        source: 'Avotek Fig 2-4-13',
        description: 'Shows the throttle valve location AFTER the venturi and BEFORE the intake manifold.',
        process: 'At idle, the throttle is nearly closed. Air velocity at the venturi is too low to draw fuel from the main nozzle. Instead, low pressure DOWNSTREAM of the throttle draws fuel from the idle jet through a separate passage.',
        keyPoints: [
            'Throttle is located AFTER the venturi, BEFORE the manifold',
            'At idle, main nozzle does NOT discharge (insufficient venturi suction)',
            'Idle fuel is drawn by manifold suction downstream of the throttle plate',
            'Main air bleed emulsifies fuel with air before discharge'
        ],
        examQs: [
            { q: 'Where is the throttle valve located relative to the venturi?', opts: ['Before the venturi', 'Inside the venturi throat', 'After the venturi and before the intake manifold', 'At the carburetor inlet'], correct: 2, explain: 'The throttle valve is downstream of the venturi but upstream of the intake manifold.' },
            { q: 'Why does the main discharge nozzle NOT flow fuel at idle?', opts: ['The fuel pump is off', 'There is insufficient airflow through the venturi to create a pressure differential', 'The mixture control blocks it', 'The float is too high'], correct: 1, explain: 'At idle, throttle is nearly closed → very little air through venturi → insufficient pressure drop to draw fuel from the main nozzle.' }
        ],
        flashcard: { front: 'Why doesn\'t the main discharge nozzle flow fuel at idle?', back: 'At idle, the throttle is nearly closed → not enough AIR through the venturi → insufficient pressure drop. Idle fuel comes from a separate idle jet, drawn by MANIFOLD suction downstream of the butterfly.' }
    },
    {
        id: 'ref-idle-pressure',
        title: 'Idle Circuit Pressure Differentials',
        category: 'schematics',
        image: 'diagrams/ref_idle_pressure.png',
        source: 'Avotek Fig 2-4-15',
        description: 'Pressure map showing 11 psi at venturi throat vs 13.5-14 psi at the float bowl during idle.',
        process: 'At idle: float bowl = 14 psi (atmospheric). Venturi throat = 13.5 psi. Downstream of throttle = 11 psi. The small differential (0.5 psi) at the venturi is NOT enough to lift fuel up the main nozzle. But the 3 psi drop downstream of the throttle IS enough to pull fuel through the idle jet.',
        keyPoints: [
            'Float bowl pressure ≈ 14 psi (atmospheric/vented)',
            'Venturi throat ≈ 13.5 psi at idle (only 0.5 psi drop — insufficient for main nozzle)',
            'Downstream of throttle ≈ 11 psi (3 psi below bowl — sufficient for idle jet)',
            'As throttle opens → venturi differential increases → main nozzle takes over'
        ],
        examQs: [
            { q: 'At idle, the pressure differential between the float bowl and venturi throat is approximately:', opts: ['5 psi', '0.5 psi', '3 psi', '14 psi'], correct: 1, explain: 'At idle: bowl ≈ 14 psi, venturi throat ≈ 13.5 psi. The difference of only 0.5 psi is too small to draw fuel from the main nozzle.' },
            { q: 'What pressure condition draws fuel through the idle jet?', opts: ['High venturi suction', 'Low manifold pressure (≈11 psi) downstream of the nearly-closed throttle', 'Engine-driven pump pressure', 'Float bowl overpressure'], correct: 1, explain: 'The nearly-closed throttle creates a significant pressure drop downstream (≈11 psi vs 14 psi bowl), drawing fuel through the idle jet.' }
        ],
        flashcard: { front: 'At idle: What draws fuel — venturi or manifold suction?', back: 'MANIFOLD SUCTION. The venturi only drops ~0.5 psi (not enough). The throttle plate blocks airflow, creating ~11 psi downstream — a 3 psi differential that pulls fuel through the idle jet.' }
    },
    {
        id: 'ref-accel-pump',
        title: 'Accelerator Pump — Internal View',
        category: 'components',
        image: 'diagrams/ref_accel_pump.png',
        source: 'Jeppesen FIG 07-31',
        description: 'Detailed cross-section of the accelerator pump system showing discharge nozzle, check valves, and pump chamber.',
        process: 'When throttle opens rapidly: pump lever pushes piston down → fuel in pump chamber pressurized → discharge check valve opens → raw fuel squirts directly into the venturi throat → prevents lean stumble.',
        keyPoints: [
            'Pump lever connected to throttle linkage',
            'Pump INLET check valve lets fuel refill the chamber when throttle closes',
            'Pump DISCHARGE check valve prevents fuel siphoning back',
            'Accelerator discharge nozzle sprays ABOVE the venturi',
            'Pump idler lever provides mechanical advantage'
        ],
        examQs: [
            { q: 'What is the purpose of the accelerator pump?', opts: ['Maintain idle mixture', 'Provide extra fuel during rapid throttle advance', 'Control mixture at altitude', 'Regulate fuel pressure'], correct: 1, explain: 'The accelerator pump compensates for the fuel lag that occurs when the throttle opens rapidly, preventing a lean stumble.' },
            { q: 'How many check valves does the accelerator pump typically have?', opts: ['None', 'One', 'Two (inlet and discharge)', 'Three'], correct: 2, explain: 'Two check valves: the inlet check valve allows the pump to refill, and the discharge check valve prevents backflow.' },
            { q: 'What happens if the accelerator pump fails?', opts: ['Nothing noticeable', 'Engine hesitates or stumbles on rapid acceleration', 'Engine runs rich at all times', 'Engine cannot idle'], correct: 1, explain: 'Without the pump, rapid throttle opening floods the engine with air but fuel lags behind → momentary lean condition → hesitation/stumble.' }
        ],
        flashcard: { front: 'Why does the engine stumble when you slam the throttle open?', back: 'The throttle opens instantly (air rushes in), but FUEL has greater mass and lags behind → momentary lean condition. The ACCELERATOR PUMP fires a raw shot of fuel to bridge this gap.' }
    },
    {
        id: 'ref-economizer',
        title: 'Full-Power Economizer System',
        category: 'schematics',
        image: 'diagrams/ref_economizer.png',
        source: 'Avotek Fig 2-4-19',
        description: 'Economizer needle enrichment circuit showing dual float chambers and fuel passages at full throttle.',
        process: 'At cruise (partial throttle): economizer valve is CLOSED → engine runs at best-economy mixture. At full power: throttle linkage mechanically opens the economizer valve → additional fuel flows through → mixture enriches for engine cooling and detonation protection.',
        keyPoints: [
            'Economizer enriches mixture ONLY at full/high power',
            'Provides extra fuel for cylinder cooling at high power settings',
            'Prevents detonation by ensuring the mixture is rich enough',
            'Spring-loaded needle valve controlled by throttle linkage',
            'At cruise it stays CLOSED for fuel efficiency'
        ],
        examQs: [
            { q: 'When is the economizer valve OPEN?', opts: ['At idle', 'At cruise power', 'At full or near-full power', 'Only during descent'], correct: 2, explain: 'The economizer opens at high power settings to enrich the mixture for cooling. At cruise, it stays closed for economy.' },
            { q: 'What is the primary purpose of the economizer system?', opts: ['Save fuel at all times', 'Provide additional fuel at high power for cooling and detonation prevention', 'Control idle mixture', 'Replace the accelerator pump'], correct: 1, explain: 'At high power, engines need extra fuel for cooling. The economizer provides this enrichment to prevent detonation and overheating.' }
        ],
        flashcard: { front: 'Economizer valve: When does it OPEN and WHY?', back: 'Opens at FULL/HIGH POWER only. Purpose: enriches mixture for cylinder COOLING and DETONATION prevention. At cruise → stays CLOSED for fuel economy.' }
    },
    {
        id: 'ref-carb-icing',
        title: 'Carburetor Icing — Downdraft',
        category: 'schematics',
        image: 'diagrams/ref_carb_icing.png',
        source: 'A&P Powerplant Textbook',
        description: 'Critical safety diagram showing ice formation in a downdraft carburetor restricting airflow to the engine.',
        process: 'As fuel evaporates in the venturi, temperature drops up to 70°F. If ambient humidity is sufficient, ice forms on the venturi walls and throttle plate → narrows the air passage → reduces engine power → if ignored, causes complete engine stoppage.',
        keyPoints: [
            'Carburetor icing can occur at OATs up to 70°F (21°C) with visible moisture',
            'Most likely in the 20°F–70°F range with high humidity',
            'Ice forms at the venturi throat and on the throttle butterfly edges',
            'First symptom: unexplained RPM drop (fixed pitch) or manifold pressure drop (constant speed)',
            'Application of carb heat melts the ice → temporary roughness → then smooth operation returns',
            'If symptoms are IGNORED, ice will cause the ENGINE TO STOP'
        ],
        examQs: [
            { q: 'At what outside air temperature range can carburetor icing occur?', opts: ['Below freezing only (< 32°F)', '20°F to 70°F with visible moisture', 'Only above 100°F', 'Only in sub-zero conditions'], correct: 1, explain: 'Carb icing can occur well above freezing due to the temperature drop from fuel evaporation. The 20°F to 70°F range with moisture is most dangerous.' },
            { q: 'What is the first indication of carburetor icing in a fixed-pitch propeller aircraft?', opts: ['Engine vibration', 'Oil temperature rise', 'Unexplained decrease in RPM', 'Fuel pressure drop'], correct: 2, explain: 'Ice restricts airflow → power drops → RPM decreases. In a constant-speed prop aircraft, manifold pressure decreases instead.' },
            { q: 'When carb heat is applied to melt ice, what initially happens?', opts: ['RPM immediately increases', 'Engine runs rough temporarily, then smooths out', 'Engine stops', 'No change'], correct: 1, explain: 'Melting ice creates water which causes temporary roughness. Then RPM increases as airflow is restored.' }
        ],
        flashcard: { front: 'Can carb icing occur on a 60°F day?', back: 'YES! Fuel evaporation drops temperature up to 70°F in the venturi. Icing is most likely between 20°F–70°F OAT with HIGH HUMIDITY. First sign: unexplained RPM drop.' }
    },
    {
        id: 'ref-amc-full',
        title: 'MA-4SPA with AMC Assembly',
        category: 'schematics',
        image: 'diagrams/ref_amc_full.jpg',
        source: 'Jeppesen FIG 07-40',
        description: 'Complete Marvel-Schebler MA-4SPA with Automatic Mixture Control and color-coded flow legend.',
        process: 'The AMC is a SEPARATE bellows-operated unit that senses air density changes. As altitude increases → air density decreases → bellows expands → reduces fuel flow automatically. This is INDEPENDENT of the manual mixture control and throttle.',
        keyPoints: [
            'AMC responds to air density changes (altitude + temperature)',
            'NOT connected to manual mixture or throttle',
            'Contains a bellows that expands with decreasing air density',
            'If AMC sticks EXTENDED during descent → mixture goes LEANER → higher CHT',
            'Color legend: Impact Air, Venturi Suction, Fuel Supply, Regulated Fuel, Metered Fuel'
        ],
        examQs: [
            { q: 'The Automatic Mixture Control (AMC) is connected to:', opts: ['The throttle linkage', 'The manual mixture control', 'Neither — it is a separate independent unit', 'The fuel pump'], correct: 2, explain: 'The AMC is a separate unit that responds to air density. It is NOT connected to the manual mixture control or throttle.' },
            { q: 'If the AMC sticks in the extended (high-altitude) position during descent:', opts: ['Mixture becomes richer', 'Mixture becomes leaner → higher cylinder head temperature', 'No effect', 'Engine runs at idle'], correct: 1, explain: 'A stuck AMC continues to lean the mixture even at low altitude where denser air requires more fuel → lean condition → higher CHT, possible detonation.' }
        ],
        flashcard: { front: 'Is the AMC connected to the manual mixture control?', back: 'NO! The AMC is a completely SEPARATE, INDEPENDENT unit. It uses a bellows to sense air density changes from altitude/temperature. It is NOT connected to the manual mixture or throttle.' }
    },
    {
        id: 'ref-amc-regulator',
        title: 'AMC Regulator Unit — Poppet Valves',
        category: 'schematics',
        image: 'diagrams/ref_amc_regulator.png',
        source: 'Jeppesen FIG 07-41',
        description: 'Internal view of the fuel regulator unit with inner/outer diaphragms, poppet valve assembly, and main metering jet.',
        process: 'Impact air pressure acts on the outer diaphragm. Venturi suction pulls on the inner diaphragm. The resulting differential acts on the poppet valve assembly (A, B, D, E) to meter fuel proportionally to airflow.',
        keyPoints: [
            'Two diaphragms: INNER and OUTER',
            'Impact air pressure pushes the outer diaphragm',
            'Venturi suction pulls the inner diaphragm',
            'Poppet valve assembly meters fuel based on the pressure differential',
            'Main metering jet is located at position C (red)',
            'Fuel metering is proportional to AIR flow through the venturi'
        ],
        examQs: [
            { q: 'In the AMC regulator, what two pressures act on the diaphragms?', opts: ['Fuel pressure and oil pressure', 'Impact air pressure and venturi suction', 'Manifold pressure and exhaust pressure', 'Boost pump pressure and gravity'], correct: 1, explain: 'The outer diaphragm senses impact (ram) air pressure, while the inner diaphragm senses venturi suction. The differential meters fuel.' }
        ],
        flashcard: { front: 'What two pressures drive the AMC regulator?', back: 'IMPACT AIR pressure on the outer diaphragm + VENTURI SUCTION on the inner diaphragm. The differential between them operates the poppet valve assembly to meter fuel proportionally to airflow.' }
    },
    {
        id: 'ref-fuel-flow-path',
        title: 'Fuel Flow Path — Discharge to Engine',
        category: 'schematics',
        image: 'diagrams/ref_fuel_flow_path.png',
        source: 'Jeppesen FIG 07-42',
        description: 'Shows the complete metered fuel path from the discharge valve through the venturi to the engine.',
        process: 'Metered fuel (red) exits the discharge valve → travels through the discharge nozzle → enters the venturi throat where it mixes with intake air. The idle needle valve provides separate fuel metering at low throttle settings.',
        keyPoints: [
            'Metered fuel shown in RED',
            'Impact air shown in LIGHT BLUE',
            'Venturi suction shown in MEDIUM BLUE',
            'Discharge nozzle sits IN the venturi throat',
            'Idle needle valve provides fuel at low power settings',
            'Throttle valve is downstream of the discharge point'
        ],
        examQs: [
            { q: 'Where does the fuel discharge nozzle deliver fuel?', opts: ['Before the air filter', 'Directly into the venturi throat', 'Into the intake manifold', 'Into the cylinder'], correct: 1, explain: 'The main discharge nozzle is positioned in the venturi throat where the low pressure draws fuel out and the high-velocity airflow atomizes it.' }
        ],
        flashcard: { front: 'Where exactly does the main discharge nozzle spray fuel?', back: 'Directly INTO the venturi throat — the narrowest point where air velocity is highest and pressure is lowest. The high-speed airflow atomizes the fuel into a fine mist for complete combustion.' }
    },
    {
        id: 'ref-amc-cutoff',
        title: 'AMC — Idle Cut-Off Position',
        category: 'schematics',
        image: 'diagrams/ref_amc_cutoff.png',
        source: 'Jeppesen FIG 07-43',
        description: 'MA-4SPA in idle cut-off position showing manual mixture control and AMC needle valve.',
        process: 'When the mixture control is pulled to IDLE CUT-OFF: the manual mixture valve completely blocks fuel flow → engine starves of fuel and shuts down cleanly. The AMC needle valve and assembly are also visible below.',
        keyPoints: [
            'IDLE CUT-OFF = fuel completely blocked by manual mixture valve',
            'This is the normal engine shutdown procedure',
            'Ensures a clean, positive shutdown (no dieseling)',
            'AMC assembly visible at bottom — separate from manual control',
            'If mixture valve does not seat perfectly → engine may diesel/continue running'
        ],
        examQs: [
            { q: 'What is the correct procedure to shut down a carbureted piston engine?', opts: ['Turn off the magnetos', 'Pull mixture to idle cut-off', 'Close the throttle fully', 'Turn off the fuel selector'], correct: 1, explain: 'The standard shutdown is to pull mixture to idle cut-off. This starves the engine of fuel for a clean shutdown and prevents dieseling.' },
            { q: 'If the engine continues to run (diesels) after pulling mixture to idle cut-off, what is the likely cause?', opts: ['Ignition switch failure', 'Mixture valve not seating properly — fuel leaking past', 'Battery overcharge', 'Carburetor heat left on'], correct: 1, explain: 'If the rotary or plunger mixture valve is scored or not lapped, fuel seeps past → engine keeps running even in cut-off.' }
        ],
        flashcard: { front: 'Engine keeps running after you pull mixture to idle cut-off. What\'s wrong?', back: 'The mixture control valve is NOT SEATING properly — fuel is leaking past a scored or improperly lapped valve seat. Solution: remove, inspect, and LAP the valve and seat.' }
    },
    {
        id: 'ref-accel-manifold',
        title: 'Accelerator Pump — Manifold Pressure',
        category: 'schematics',
        image: 'diagrams/ref_accel_manifold.png',
        source: 'Jeppesen FIG 07-44',
        description: 'Shows the accelerator pump fuel path with manifold pressure inlet and bleed systems.',
        process: 'The bleed air and check valve system work together to provide the correct fuel/air ratio during acceleration. Manifold pressure feeds into the system through the manifold pressure inlet.',
        keyPoints: [
            'Manifold pressure inlet at top right',
            'Bleed system adds air to the fuel stream for emulsification',
            'Check valve prevents reverse flow',
            'Venturi tube creates the pressure differential for fuel draw',
            'Metered fuel shown in RED, manifold pressure in LIGHT BLUE'
        ],
        examQs: [
            { q: 'What is the purpose of bleed air in the fuel delivery system?', opts: ['Cool the fuel', 'Emulsify fuel with air for better atomization', 'Increase fuel pressure', 'Prevent icing'], correct: 1, explain: 'Bleed air mixes with fuel before it exits the nozzle, creating an emulsion that atomizes better than liquid fuel alone.' }
        ],
        flashcard: { front: 'What does "bleed air" do in the carburetor fuel circuit?', back: 'Bleed air EMULSIFIES the fuel — mixing it with air BEFORE it exits the nozzle. This breaks the fuel into finer droplets for better atomization and more complete combustion.' }
    },
    {
        id: 'ref-idle-adjustments',
        title: 'Idle Speed & Mixture Adjustments',
        category: 'components',
        image: 'diagrams/ref_idle_adjustments.png',
        source: 'Jeppesen FIG 07-47',
        description: 'External view of the idle adjustment screws and throttle stop on the carburetor body.',
        process: 'The IDLE SPEED adjustment sets the throttle stop position (how far open the throttle rests at idle). The IDLE MIXTURE adjustment screw controls the fuel/air ratio at idle. Proper adjustment: lean idle mixture until RPM starts to drop, then richen until 25-50 RPM rise before cut-off.',
        keyPoints: [
            'IDLE SPEED = throttle stop (sets minimum throttle opening)',
            'IDLE MIXTURE = fuel/air ratio at idle (screw adjusts fuel flow)',
            'Idle control shaft connects to cockpit vernier',
            'Correct idle mixture: 25-50 RPM rise just before engine dies at cut-off',
            'These are EXTERNAL adjustments — no disassembly required'
        ],
        examQs: [
            { q: 'How do you check for correct idle mixture on a float carburetor?', opts: ['Measure fuel pressure', 'Slowly lean mixture; look for 25-50 RPM rise before engine dies', 'Check exhaust gas color', 'Measure CHT'], correct: 1, explain: 'At idle cut-off, a properly set idle mixture will show a 25-50 RPM rise (momentary enrichment) just before the engine stops.' },
            { q: 'The idle speed adjustment screw controls:', opts: ['Fuel flow at idle', 'The minimum throttle butterfly opening (throttle stop)', 'Main jet size', 'Float level'], correct: 1, explain: 'The idle SPEED screw is a throttle stop — it sets how far closed the throttle can go, determining idle RPM.' }
        ],
        flashcard: { front: 'Idle SPEED vs Idle MIXTURE — what does each adjust?', back: 'IDLE SPEED = throttle stop (how far butterfly closes). IDLE MIXTURE = fuel/air ratio at idle. Correct mixture check: lean to cut-off → look for 25-50 RPM RISE before engine dies.' }
    }
];

// ===== FLASHCARD STATE =====
let flashcardIndex = 0;
let flashcardFlipped = false;
let flashcardDeck = [];
let studyProgress = {};

// ===== GALLERY STATE =====
let currentFilter = 'all';
let viewerState = { zoom: 1, panX: 0, panY: 0, isDragging: false, startX: 0, startY: 0 };

// ===== SETUP =====
export function setupLibrary() {
    loadProgress();
    renderGallery();
    setupCategoryFilters();
    setupViewerControls();
    setupFlashcardControls();
}

function loadProgress() {
    try { studyProgress = JSON.parse(localStorage.getItem('carb-study-progress') || '{}'); } catch { studyProgress = {}; }
}
function saveProgress() {
    try { localStorage.setItem('carb-study-progress', JSON.stringify(studyProgress)); } catch {}
}

// ===== CATEGORY FILTERING =====
function setupCategoryFilters() {
    const catContainer = document.getElementById('library-cats');
    catContainer?.querySelectorAll('.lib-cat-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            catContainer.querySelector('.active')?.classList.remove('active');
            btn.classList.add('active');
            currentFilter = btn.dataset.category;
            renderGallery();
        });
    });
}

// ===== GALLERY =====
function renderGallery() {
    const container = document.getElementById('library-gallery');
    if (!container) return;

    const filtered = currentFilter === 'all' ? REFERENCES : REFERENCES.filter(r => r.category === currentFilter);
    const studied = studyProgress;

    container.innerHTML = `
        ${filtered.map(ref => {
            const isStudied = studied[ref.id];
            return `
            <div class="ref-card ${isStudied ? 'studied' : ''}" data-ref-id="${ref.id}">
                <div class="ref-thumb-wrap">
                    <img src="${ref.image}" alt="${ref.title}" loading="lazy">
                    ${isStudied ? '<span class="studied-badge">✓ Studied</span>' : ''}
                </div>
                <div class="ref-info">
                    <h4>${ref.title}</h4>
                    <p>${ref.description}</p>
                    <div class="ref-meta">
                        <span class="ref-source">${ref.source}</span>
                        ${ref.examQs ? `<span class="ref-q-count">${ref.examQs.length} MCQ${ref.examQs.length > 1 ? 's' : ''}</span>` : ''}
                    </div>
                </div>
            </div>`;
        }).join('')}
    `;

    // Wire click events
    container.querySelectorAll('.ref-card').forEach(card => {
        card.addEventListener('click', () => openViewer(card.dataset.refId));
    });

    window.openRefViewer = openViewer;
}

// ===== INTERACTIVE VIEWER =====
function setupViewerControls() {
    document.getElementById('viewer-close-btn')?.addEventListener('click', closeViewer);
    document.querySelector('#library-viewer .viewer-backdrop')?.addEventListener('click', closeViewer);
    document.getElementById('viewer-zoom-in')?.addEventListener('click', () => adjustZoom(0.25));
    document.getElementById('viewer-zoom-out')?.addEventListener('click', () => adjustZoom(-0.25));
    document.getElementById('viewer-reset')?.addEventListener('click', resetView);

    const viewport = document.getElementById('viewer-viewport');
    viewport?.addEventListener('mousedown', startPan);
    viewport?.addEventListener('touchstart', e => { const t = e.touches[0]; startPan({ clientX: t.clientX, clientY: t.clientY }); }, { passive: true });
    window.addEventListener('mousemove', handlePan);
    window.addEventListener('touchmove', e => { const t = e.touches[0]; handlePan({ clientX: t.clientX, clientY: t.clientY }); }, { passive: true });
    window.addEventListener('mouseup', stopPan);
    window.addEventListener('touchend', stopPan);
    viewport?.addEventListener('wheel', handleScrollZoom, { passive: false });
}

function openViewer(refId) {
    const ref = REFERENCES.find(r => r.id === refId);
    if (!ref) return;

    // Mark as studied
    studyProgress[refId] = Date.now();
    saveProgress();

    const modal = document.getElementById('library-viewer');
    const img = document.getElementById('viewer-img');
    const title = document.getElementById('viewer-title');
    if (!modal || !img || !title) return;

    title.textContent = ref.title;
    img.src = ref.image;
    modal.classList.remove('hidden');
    resetView();

    // Build explanation + exam panel
    renderExplanationPanel(ref);
}

function renderExplanationPanel(ref) {
    let panel = document.getElementById('viewer-explain-panel');
    if (!panel) {
        panel = document.createElement('div');
        panel.id = 'viewer-explain-panel';
        panel.className = 'viewer-explain-panel';
        document.querySelector('#library-viewer .viewer-body')?.appendChild(panel);
    }

    panel.innerHTML = `
        <div class="explain-tabs">
            <button class="explain-tab active" data-tab="explain">📖 Explanation</button>
            <button class="explain-tab" data-tab="exam">❓ Exam MCQs</button>
            <button class="explain-tab" data-tab="flash">🃏 Flashcard</button>
        </div>
        <div class="explain-body" id="explain-body">
            ${buildExplainContent(ref)}
        </div>
    `;

    // Tab switching
    panel.querySelectorAll('.explain-tab').forEach(tab => {
        tab.addEventListener('click', () => {
            panel.querySelectorAll('.explain-tab').forEach(t => t.classList.remove('active'));
            tab.classList.add('active');
            const body = document.getElementById('explain-body');
            if (tab.dataset.tab === 'explain') body.innerHTML = buildExplainContent(ref);
            else if (tab.dataset.tab === 'exam') body.innerHTML = buildExamContent(ref);
            else if (tab.dataset.tab === 'flash') body.innerHTML = buildFlashContent(ref);
            wireExamListeners(ref);
            wireFlashListeners(ref);
        });
    });
}

function buildExplainContent(ref) {
    return `
        <div class="explain-section">
            <div class="explain-source">${ref.source}</div>
            <h4>Process</h4>
            <p>${ref.process}</p>
        </div>
        <div class="explain-section">
            <h4>🎯 Key Points for Exam</h4>
            <ul>${ref.keyPoints.map(kp => `<li>${kp}</li>`).join('')}</ul>
        </div>
    `;
}

function buildExamContent(ref) {
    if (!ref.examQs || ref.examQs.length === 0) return '<p class="no-exam">No exam questions for this reference.</p>';
    return ref.examQs.map((q, i) => `
        <div class="exam-q-block" data-qi="${i}">
            <div class="exam-q-text"><strong>Q${i + 1}:</strong> ${q.q}</div>
            <div class="exam-opts">
                ${q.opts.map((opt, oi) => `<button class="exam-opt-btn" data-qi="${i}" data-oi="${oi}">${String.fromCharCode(65 + oi)}) ${opt}</button>`).join('')}
            </div>
            <div class="exam-feedback hidden" id="exam-fb-${i}"></div>
        </div>
    `).join('');
}

function wireExamListeners(ref) {
    document.querySelectorAll('.exam-opt-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const qi = parseInt(btn.dataset.qi);
            const oi = parseInt(btn.dataset.oi);
            const q = ref.examQs[qi];
            const fb = document.getElementById(`exam-fb-${qi}`);
            const block = btn.closest('.exam-q-block');

            block.querySelectorAll('.exam-opt-btn').forEach(b => {
                b.classList.add('disabled');
                b.disabled = true;
                if (parseInt(b.dataset.oi) === q.correct) b.classList.add('correct');
            });

            if (oi === q.correct) {
                btn.classList.add('correct');
                fb.innerHTML = `<span class="correct-text">✅ Correct!</span> ${q.explain}`;
            } else {
                btn.classList.add('incorrect');
                fb.innerHTML = `<span class="incorrect-text">❌ Incorrect.</span> ${q.explain}`;
            }
            fb.classList.remove('hidden');
        });
    });
}

function buildFlashContent(ref) {
    if (!ref.flashcard) return '<p>No flashcard for this reference.</p>';
    return `
        <div class="single-flashcard" id="single-fc">
            <div class="fc-front">${ref.flashcard.front}</div>
            <div class="fc-back hidden">${ref.flashcard.back}</div>
        </div>
        <button class="fc-flip-btn" id="fc-flip-single">Tap to Reveal Answer</button>
    `;
}

function wireFlashListeners(ref) {
    document.getElementById('fc-flip-single')?.addEventListener('click', () => {
        const fc = document.getElementById('single-fc');
        fc?.querySelector('.fc-back')?.classList.toggle('hidden');
        fc?.querySelector('.fc-front')?.classList.toggle('hidden');
        const btn = document.getElementById('fc-flip-single');
        if (btn) btn.textContent = btn.textContent.includes('Reveal') ? 'Show Question Again' : 'Tap to Reveal Answer';
    });
}

function closeViewer() {
    document.getElementById('library-viewer')?.classList.add('hidden');
    renderGallery(); // Re-render to show studied badges
}

// ===== FLASHCARD RAPID-FIRE =====
function setupFlashcardControls() {
    // These get wired when the flashcard modal is launched
}


// ===== PAN/ZOOM =====
function adjustZoom(delta) { viewerState.zoom = Math.max(0.3, Math.min(6, viewerState.zoom + delta)); updateTransform(); }
function resetView() { viewerState = { zoom: 1, panX: 20, panY: 20, isDragging: false, startX: 0, startY: 0 }; updateTransform(); }
function updateTransform() {
    const wrap = document.getElementById('viewer-canvas-wrap');
    const zt = document.getElementById('zoom-percent');
    if (wrap) wrap.style.transform = `translate(${viewerState.panX}px, ${viewerState.panY}px) scale(${viewerState.zoom})`;
    if (zt) zt.textContent = `${Math.round(viewerState.zoom * 100)}%`;
}
function startPan(e) { viewerState.isDragging = true; viewerState.startX = e.clientX - viewerState.panX; viewerState.startY = e.clientY - viewerState.panY; }
function handlePan(e) { if (!viewerState.isDragging) return; viewerState.panX = e.clientX - viewerState.startX; viewerState.panY = e.clientY - viewerState.startY; updateTransform(); }
function stopPan() { viewerState.isDragging = false; }
function handleScrollZoom(e) { e.preventDefault(); adjustZoom(e.deltaY > 0 ? -0.15 : 0.15); }
